import { useConversionReportListRefresh } from '../../utils/useConversionReportListRefresh';
import { uppercaseConversionCapture } from '../../utils/conversionCaptureText';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import { readJson, removeKey, writeJson } from '../../utils/storage';
import { getBusinessDateISO } from '../../utils/businessDate';
import { isConversionMachine } from '../../utils/conversionMachines';
import { addNovolexLogo } from '../../utils/pdfBranding';
import {
  formatConversionDowntimeValidation,
  formatWorkOrderIssue,
  validateConversionDowntime,
  validateConversionWorkOrders,
} from '../../utils/conversionWorkOrderValidation';
import {
  fillExistingPaperRollSeries,
  formatPaperRollIssue,
  validatePaperRollSeries,
} from '../../utils/conversionPaperRollValidation';
import {
  dequeueConversionReportSync,
  enqueueConversionReportSync,
  getPendingConversionReportCount,
  listLocalConversionReports,
  loadConversionReportQueue,
  loadConversionReportRecords,
  makeConversionReportKey,
  purgeStaleLocalRecords,
  removeLocalConversionReport,
  upsertLocalConversionReport
} from '../../utils/offlineConversion';
import ConversionProductionGrid from './ConversionProductionGrid';
import ConversionDowntimeGrid from './ConversionDowntimeGrid';
import ConversionMaterialTraceability from './ConversionMaterialTraceability';
import {
  materialLabel, materialPalletLabel, normalizeMaterialTraceability, validateMaterialTraceability,
} from '../../utils/conversionMaterialTraceability';
import { advanceReportFieldOnEnter } from '../../utils/enterAsTab';
import ConversionKpiPanel from './ConversionKpiPanel';
import SearchableCatalogSelect from '../common/SearchableCatalogSelect';

const getEmployeeNumber = (employee) => employee.employee_number;
const getEmployeeLabel = (employee) => `${employee.employee_number} - ${employee.name}`;
const getEmployeeAliases = (employee) => [employee.employee_number, employee.name];
const employeeNumberPattern = /^\d+$/;
const resolveEmployeeNumber = (header, people, role) => {
  const savedNumber = header[`${role}_employee_number`];
  if (savedNumber) return String(savedNumber);
  const selectedPerson = people.find((person) => String(person.id) === String(header[`${role}_id`]));
  return selectedPerson?.employee_number ? String(selectedPerson.employee_number) : '';
};

const CONVERSION_DEBUG = true;
const AUTO_SAVE_DELAY_MS = 5000;
const conversionLog = (...args) => {
  if (CONVERSION_DEBUG) console.log('[ConversionCapture]', ...args);
};

const getISOWeekNumber = (dateISO) => {
  if (!dateISO) return null;
  const [y, m, d] = dateISO.split('-').map((v) => parseInt(v, 10));
  if (!y || !m || !d) return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return weekNo;
};

const shiftISODate = (dateISO, days) => {
  const [year, month, day] = String(dateISO || '').split('-').map(Number);
  if (!year || !month || !day) return '';
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatOperationalDate = (dateISO, options) => {
  const [year, month, day] = String(dateISO || '').split('-').map(Number);
  if (!year || !month || !day) return '—';
  return new Intl.DateTimeFormat('es-MX', options).format(new Date(year, month - 1, day));
};

const toInt = (v) => {
  const n = parseInt(String(v ?? 0), 10);
  return Number.isFinite(n) ? n : 0;
};

const normalizeItemValue = (v) => String(v ?? '').trim().toUpperCase();
const numericItemValue = (v) => {
  const txt = String(v ?? '').trim();
  if (!/^\d+$/.test(txt)) return null;
  const n = parseInt(txt, 10);
  return Number.isFinite(n) ? String(n) : null;
};
const itemValuesMatch = (a, b) => {
  const na = normalizeItemValue(a);
  const nb = normalizeItemValue(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const da = numericItemValue(na);
  const db = numericItemValue(nb);
  return da != null && db != null && da === db;
};

const normalizeArray = (v) => (Array.isArray(v) ? v : []);
const normalizeIdentity = (v) => String(v ?? '').trim().toLowerCase();

const normalizeDateValue = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  if (!s) return '';
  // Accept YYYY-MM-DD directly
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // Handle ISO/date-time values from backend serializers
  if (s.includes('T')) return s.slice(0, 10);
  // Fallback: try Date parse
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const computeProducedQuantity = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const good = list.reduce((acc, r) => acc + toInt(r.bags_good), 0);
  if (good > 0) return good;
  const counter = list.reduce((acc, r) => acc + toInt(r.counter), 0);
  if (counter > 0) return counter;
  return list.reduce((acc, r) => acc + toInt(r.quantity), 0);
};

const defaultHeader = (dateISO) => ({
  date: dateISO,
  shift_id: '',
  line_id: '',
  week_override: '',
  total_time_min: 0,
  adjuster_id: '',
  adjuster_employee_number: '',
  machinist_id: '',
  machinist_employee_number: '',
  receiver_id: '',
  receiver_employee_number: '',
  group_leader_id: '',
  group_leader_employee_number: '',
  status: 'open',
  closed_at: null,
  closed_by: null,
});

const normalizeConversionRecord = (record, fallbackHeader) => {
  const rec = record || {};
  const hdr = rec.conversion_header || {};
  const rowList = fillExistingPaperRollSeries(normalizeArray(rec.rows));
  const dtList = normalizeArray(rec.downtime?.length ? rec.downtime : rec.downtimes);

  return {
    header: {
      ...fallbackHeader,
      date: normalizeDateValue(rec.date) || fallbackHeader.date,
      line_id: rec.line_id != null ? String(rec.line_id) : fallbackHeader.line_id,
      shift_id: rec.shift_id != null ? String(rec.shift_id) : fallbackHeader.shift_id,
      week_override: rec.week_override || hdr.week_override || fallbackHeader.week_override || '',
      total_time_min: rec.total_time_min ?? hdr.total_time_min ?? fallbackHeader.total_time_min ?? 0,
      adjuster_id: rec.adjuster_id != null ? String(rec.adjuster_id) : (hdr.adjuster_id != null ? String(hdr.adjuster_id) : fallbackHeader.adjuster_id),
      adjuster_employee_number: rec.adjuster_employee_number ?? hdr.adjuster_employee_number ?? fallbackHeader.adjuster_employee_number ?? '',
      machinist_id: rec.machinist_id != null ? String(rec.machinist_id) : (hdr.machinist_id != null ? String(hdr.machinist_id) : fallbackHeader.machinist_id),
      machinist_employee_number: rec.machinist_employee_number ?? hdr.machinist_employee_number ?? fallbackHeader.machinist_employee_number ?? '',
      receiver_id: rec.receiver_id != null ? String(rec.receiver_id) : (hdr.receiver_id != null ? String(hdr.receiver_id) : fallbackHeader.receiver_id),
      receiver_employee_number: rec.receiver_employee_number ?? hdr.receiver_employee_number ?? fallbackHeader.receiver_employee_number ?? '',
      group_leader_id: rec.group_leader_id != null ? String(rec.group_leader_id) : (hdr.group_leader_id != null ? String(hdr.group_leader_id) : fallbackHeader.group_leader_id),
      group_leader_employee_number: rec.group_leader_employee_number ?? hdr.group_leader_employee_number ?? fallbackHeader.group_leader_employee_number ?? '',
      status: rec.status || hdr.status || fallbackHeader.status || 'open',
      closed_at: rec.closed_at ?? hdr.closed_at ?? fallbackHeader.closed_at ?? null,
      closed_by: rec.closed_by ?? hdr.closed_by ?? fallbackHeader.closed_by ?? null,
      created_by: rec.created_by ?? hdr.created_by ?? fallbackHeader.created_by ?? null,
      week: rec.week ?? hdr.week ?? fallbackHeader.week ?? null,
    },
    rows: rowList,
    downtime: dtList,
    material_traceability: normalizeArray(rec.material_traceability),
  };
};

const ConversionCapture = () => {
  const { isAuthenticated, loading: authLoading, user, can } = useAuth();
  const { getCurrentSiteId, currentSite, loading: siteLoading } = useSite();
  const navigate = useNavigate();
  const hydratingRef = useRef(false);
  const skipAutosaveOnceRef = useRef(false);

  const rawSiteId = getCurrentSiteId();
  const siteId = rawSiteId == null || String(rawSiteId).trim() === '' ? 1 : rawSiteId;
  const canCaptureConversion = can('conversion:capture');
  const canCloseShift = can('conversion:view') && canCaptureConversion;
  const catalogsCacheKey = (sid) => `catalogs_all_cache_v1:${sid ?? 'null'}`;
  const usersCacheKey = (sid) => `users_lookup_cache_v1:${sid ?? 'null'}`;
  const lastReportKey = (userId) => `conversion_last_report_v1:${userId ?? 'anon'}`;

  const [lines, setLines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [products, setProducts] = useState([]);
  const [downtimeCodes, setDowntimeCodes] = useState([]);
  const [people, setPeople] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | dirty | saving | saved | pending | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deletingReport, setDeletingReport] = useState(false);
  const [showDeleteReportModal, setShowDeleteReportModal] = useState(false);
  const [deleteConfirmCode, setDeleteConfirmCode] = useState('');
  const [deleteCodeInput, setDeleteCodeInput] = useState('');
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopeningReport, setReopeningReport] = useState(false);
  const [activeTab, setActiveTab] = useState('produccion'); // produccion | ax | kpis

  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(getPendingConversionReportCount());
  const [lastSyncAt, setLastSyncAt] = useState(null);

  const defaultBusinessDate = getBusinessDateISO();
  const [header, setHeader] = useState(() => defaultHeader(defaultBusinessDate));
  const [rows, setRows] = useState([]);
  const [downtime, setDowntime] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [orderDataLookup, setOrderDataLookup] = useState(() => new Map());

  const [availableReports, setAvailableReports] = useState([]);
  const [reportSearchFilter, setReportSearchFilter] = useState('');
  const [reportViewTab, setReportViewTab] = useState('mine'); // mine | others

  const findEmployeeByIdentity = useCallback((identity) => {
    if (identity == null || identity === '') return null;
    const raw = String(identity).trim();
    const norm = normalizeIdentity(identity);
    return people.find((p) => {
      if (p?.employee_number != null && String(p.employee_number).trim() === raw) return true;
      if (p?.id != null && String(p.id).trim() === raw) return true;
      if (normalizeIdentity(p?.username) === norm) return true;
      if (normalizeIdentity(p?.email) === norm) return true;
      return false;
    }) || null;
  }, [people]);

  // Track current user's employee number for highlighting their report
  const currentUserEmployeeNumber = useMemo(() => {
    if (user?.employee_number) return String(user.employee_number);

    const candidates = [user?.username, user?.email, user?.id];
    for (const c of candidates) {
      const emp = findEmployeeByIdentity(c);
      if (emp?.employee_number) return String(emp.employee_number);
    }

    return null;
  }, [findEmployeeByIdentity, user]);

  const getReportOwnerEmployeeNumber = useCallback((report) => {
    const rawCreatedBy = report?.created_by;
    if (rawCreatedBy == null || rawCreatedBy === '') return null;

    const byRawEmpNo = people.find((p) => p?.employee_number != null && String(p.employee_number).trim() === String(rawCreatedBy).trim());
    if (byRawEmpNo?.employee_number) return String(byRawEmpNo.employee_number);

    const mapped = findEmployeeByIdentity(rawCreatedBy);
    if (mapped?.employee_number) return String(mapped.employee_number);

    return String(rawCreatedBy).trim();
  }, [findEmployeeByIdentity, people]);

  const showReportOwnerTab = useCallback((report) => {
    const ownerEmployeeNumber = getReportOwnerEmployeeNumber(report);
    if (!currentUserEmployeeNumber || !ownerEmployeeNumber) return;

    setReportViewTab(
      String(ownerEmployeeNumber) === String(currentUserEmployeeNumber) ? 'mine' : 'others'
    );
  }, [currentUserEmployeeNumber, getReportOwnerEmployeeNumber]);

  const computedWeek = useMemo(() => getISOWeekNumber(header.date), [header.date]);
  const businessToday = getBusinessDateISO();
  const recentOperationalDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => shiftISODate(businessToday, index - 6)),
    [businessToday]
  );
  const effectiveWeek = useMemo(() => {
    const override = parseInt(header.week_override, 10);
    if (Number.isFinite(override) && override > 0) return override;
    return computedWeek;
  }, [computedWeek, header.week_override]);

  const readOnly = header.status === 'closed' || !canCaptureConversion;

  const currentKey = useMemo(() => {
    if (!siteId || !header.date || !header.line_id || !header.shift_id) return '';
    return makeConversionReportKey({
      site_id: siteId,
      date: header.date,
      line_id: header.line_id,
      shift_id: header.shift_id
    });
  }, [header.date, header.line_id, header.shift_id, siteId]);

  const selectedLine = useMemo(() => lines.find((l) => String(l.id) === String(header.line_id)), [lines, header.line_id]);
  const selectedShift = useMemo(() => shifts.find((s) => String(s.id) === String(header.shift_id)), [shifts, header.shift_id]);
  const productsByItem = useMemo(() => {
    const map = new Map();
    (Array.isArray(products) ? products : []).forEach((p) => {
      const key = String(p?.item || '').trim();
      if (!key) return;
      map.set(key, p);
    });
    return map;
  }, [products]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const uniqueItems = Array.from(new Set(
        (Array.isArray(rows) ? rows : [])
          .map((r) => String(r?.item || '').trim())
          .filter(Boolean)
      ));

      if (uniqueItems.length === 0) {
        setOrderDataLookup(new Map());
        return;
      }

      const nextMap = new Map();
      await Promise.all(uniqueItems.map(async (item) => {
        const params = { search: item, per_page: 25, page: 1 };
        if (siteId) params.site_id = siteId;
        try {
          const { data } = await axios.get('/orders-data', { params });
          const options = Array.isArray(data?.data) ? data.data : [];
          const exact = options.find((o) => itemValuesMatch(o?.item, item));
          if (exact) {
            nextMap.set(item, exact);
          }
        } catch (e) {
          // ignore per-item metadata errors
        }
      }));

      if (!cancelled) {
        setOrderDataLookup(nextMap);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [rows, siteId]);
  
  // Filter people to only show those with employee_number
  const filteredPeople = useMemo(() => 
    people.filter((p) => p.employee_number && String(p.employee_number).trim() !== ''),
    [people]
  );

  const setEmployeeAssignment = (role, employeeNumber) => {
    const normalizedNumber = String(employeeNumber ?? '').trim();
    const selectedPerson = filteredPeople.find(
      (person) => String(person.employee_number).trim() === normalizedNumber
    );
    setHeader((prev) => ({
      ...prev,
      [`${role}_id`]: selectedPerson ? String(selectedPerson.id) : '',
      [`${role}_employee_number`]: normalizedNumber,
    }));
  };

  const refreshPendingCount = useCallback(() => setPendingCount(getPendingConversionReportCount()), []);

  const applyLoadedRecord = useCallback(
    (record) => {
      skipAutosaveOnceRef.current = true;
      const base = defaultHeader(header.date || defaultBusinessDate);
      const parsed = normalizeConversionRecord(record, base);

      const parsedDate = parsed?.header?.date;
      const parsedLineId = parsed?.header?.line_id;
      const parsedShiftId = parsed?.header?.shift_id;

      const resolvedDate = parsedDate != null && String(parsedDate).trim() !== '' ? parsedDate : (record?.date || null);
      const resolvedLineId = parsedLineId != null && String(parsedLineId).trim() !== ''
        ? String(parsedLineId)
        : (record?.line_id != null ? String(record.line_id) : null);
      const resolvedShiftId = parsedShiftId != null && String(parsedShiftId).trim() !== ''
        ? String(parsedShiftId)
        : (record?.shift_id != null ? String(record.shift_id) : null);

      hydratingRef.current = true;
      setHeader((prev) => ({
        ...prev,
        ...parsed.header,
        date: resolvedDate || prev.date,
        line_id: resolvedLineId || prev.line_id,
        shift_id: resolvedShiftId || prev.shift_id,
      }));
      setRows(parsed.rows);
      setDowntime(parsed.downtime);
      setMaterials(parsed.material_traceability);

      if (record?.updated_at) setLastSavedAt(new Date(record.updated_at).toLocaleString());
      const localStatus = record?._local?.status;
      setSaveState(localStatus === 'pending' ? 'pending' : localStatus === 'error' ? 'error' : 'saved');

      Promise.resolve().then(() => {
        hydratingRef.current = false;
      });
    },
    [defaultBusinessDate, header.date]
  );

  const fetchCatalogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (siteId) params.site_id = siteId;
      const { data } = await axios.get('/catalogs/all', { params });

      // Sort lines by code (alphanumeric)
      const sortedLines = (data.lines || []).filter(isConversionMachine).sort((a, b) => {
        const codeA = String(a.code || '').toLowerCase();
        const codeB = String(b.code || '').toLowerCase();
        return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
      });

      setLines(sortedLines);
      setShifts(data.shifts || []);
      setProducts(data.products || []);
      setDowntimeCodes(data.downtime_codes || []);

      writeJson(catalogsCacheKey(siteId), { ...data, savedAt: Date.now() });
      setIsOffline(false);
    } catch (err) {
      conversionLog('Error fetching catalogs', {
        status: err?.response?.status,
        message: err?.message,
      });
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }

      const cached = readJson(catalogsCacheKey(siteId), null);
      if (cached) {
        // Sort cached lines as well
        const sortedLines = (cached.lines || []).filter(isConversionMachine).sort((a, b) => {
          const codeA = String(a.code || '').toLowerCase();
          const codeB = String(b.code || '').toLowerCase();
          return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
        });
        
        setLines(sortedLines);
        setShifts(cached.shifts || []);
        setProducts(cached.products || []);
        setDowntimeCodes(cached.downtime_codes || []);
      }

      if (!err.response) setIsOffline(true);
    } finally {
      setLoading(false);
    }
  }, [navigate, siteId]);

  const fetchPeople = useCallback(async () => {
    try {
      const params = {
        per_page: 'all',
        user_type: 'employee',
        _fresh: Date.now(),
      };
      if (siteId) params.site_id = siteId;
      const { data } = await axios.get('/users/lookup', {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      const list = Array.isArray(data) ? data : [];
      setPeople(list);
      writeJson(usersCacheKey(siteId), { users: list, savedAt: Date.now() });
      setIsOffline(false);
    } catch (err) {
      conversionLog('Error fetching employees', {
        status: err?.response?.status,
        message: err?.message,
      });
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      const cached = readJson(usersCacheKey(siteId), null);
      if (cached?.users) setPeople(cached.users);
      if (!err.response) setIsOffline(true);
    }
  }, [navigate, siteId]);

  const fetchReportsForDate = useCallback(async ({ forceRemote = false } = {}) => {
    if (!siteId || !header.date) return;

    // Local first: include pending drafts so "capturas" se vean aunque no haya sync.
    const localDrafts = listLocalConversionReports({ site_id: siteId, date: header.date })
      .map((r) => {
        const parsed = normalizeConversionRecord(r, defaultHeader(header.date));
        return {
          id: r?.id || null,
          site_id: siteId,
          date: header.date,
          line_id: r?.line_id,
          shift_id: r?.shift_id,
          status: parsed.header.status || 'open',
          week: parsed.header.week ?? null,
          week_override: parsed.header.week_override ?? null,
          adjuster_id: parsed.header.adjuster_id ?? null,
          adjuster_employee_number: parsed.header.adjuster_employee_number ?? null,
          machinist_id: parsed.header.machinist_id ?? null,
          machinist_employee_number: parsed.header.machinist_employee_number ?? null,
          receiver_id: parsed.header.receiver_id ?? null,
          receiver_employee_number: parsed.header.receiver_employee_number ?? null,
          group_leader_id: parsed.header.group_leader_id ?? null,
          group_leader_employee_number: parsed.header.group_leader_employee_number ?? null,
          closed_at: parsed.header.closed_at ?? null,
          closed_by: parsed.header.closed_by ?? null,
          created_by: parsed.header.created_by || currentUserEmployeeNumber,
          updated_at: r?._local?.updatedAt ? new Date(r._local.updatedAt).toISOString() : r?.updated_at,
          _local: r?._local || null,
        };
      })
      .filter((r) => r.line_id && r.shift_id);

    if (isOffline && !forceRemote) {
      setAvailableReports(localDrafts);
      return;
    }

    try {
      const params = { site_id: siteId, date: header.date };
      const { data } = await axios.get('/conversion/reports', { params });
      const serverList = Array.isArray(data) ? data : [];

      const byKey = new Map();
      localDrafts.forEach((r) => {
        const k = makeConversionReportKey({ site_id: siteId, date: r.date, line_id: r.line_id, shift_id: r.shift_id });
        byKey.set(k, r);
      });
      serverList.forEach((r) => {
        const k = makeConversionReportKey({ site_id: siteId, date: r.date, line_id: r.line_id, shift_id: r.shift_id });
        byKey.set(k, { ...r, created_by: r.created_by || null });
      });

      setAvailableReports(Array.from(byKey.values()));
      setIsOffline(false);
    } catch (err) {
      conversionLog('Error fetching reports', {
        status: err?.response?.status,
        message: err?.message,
        date: header.date,
        siteId,
      });
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      if (!err.response) setIsOffline(true);
      setAvailableReports(localDrafts);
    }
  }, [header.date, isOffline, navigate, siteId]);

  const loadReport = useCallback(async () => {
    if (!siteId || !header.date || !header.line_id || !header.shift_id) return;

    const key = makeConversionReportKey({
      site_id: siteId,
      date: header.date,
      line_id: header.line_id,
      shift_id: header.shift_id
    });

    const local = loadConversionReportRecords()[key];
    // Only use local data if offline or if the record is pending sync
    const localIsPending = local?._local?.status === 'pending';
    if (local && (isOffline || localIsPending)) {
      conversionLog('Loading local report (offline or pending)', { key });
      showReportOwnerTab(local);
      applyLoadedRecord(local);
      if (isOffline) return;
    }

    // When online, always check the server for the latest data
    if (!isOffline) {
      // Start with empty state while fetching to avoid showing stale local data
      if (!localIsPending) {
        hydratingRef.current = true;
        skipAutosaveOnceRef.current = true;
        setHeader((prev) => ({
          ...defaultHeader(prev.date),
          line_id: prev.line_id,
          shift_id: prev.shift_id,
        }));
        setRows([]);
        setDowntime([]);
        setMaterials([]);
        setSaveState('idle');
        Promise.resolve().then(() => {
          hydratingRef.current = false;
        });
      }

      try {
        const params = { site_id: siteId, date: header.date, line_id: header.line_id, shift_id: header.shift_id };
        const { data } = await axios.get('/conversion/report', { params });
        conversionLog('Loaded server report', {
          key,
          id: data?.id,
          hasRows: Array.isArray(data?.rows) ? data.rows.length : 0,
        });
        upsertLocalConversionReport(data, 'synced');
        showReportOwnerTab(data);
        applyLoadedRecord(data);
        setIsOffline(false);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login');
          return;
        }
        if (err.response?.status === 404) {
          // Server says no report exists - clean up stale local copy if it was synced
          if (local && !localIsPending) {
            removeLocalConversionReport(key);
          }
          return;
        }
        conversionLog('Error loading report', {
          status: err?.response?.status,
          message: err?.message,
          key,
        });
        if (!err.response) setIsOffline(true);
        // Fallback to local if server unreachable
        if (local) applyLoadedRecord(local);
      }
    }
  }, [applyLoadedRecord, header.date, header.line_id, header.shift_id, isOffline, navigate, showReportOwnerTab, siteId]);

  const saveNow = useCallback(
    async ({ silent = false, headerOverride = null } = {}) => {
      if (!siteId) {
        if (!silent) toast.error('No hay sitio seleccionado');
        return false;
      }
      
      // Use headerOverride if provided, otherwise use current header state
      const activeHeader = headerOverride || header;
      
      if (!activeHeader.date || !activeHeader.line_id || !activeHeader.shift_id) {
        if (!silent) toast.error('Fecha, turno y máquina son obligatorios');
        return false;
      }

      const paperRollValidation = validatePaperRollSeries(rows, { requireAllRows: activeHeader.status === 'closed' });
      const blockingPaperRollIssues = paperRollValidation.issues.filter((issue) => (
        activeHeader.status === 'closed' || !['roll_missing_series', 'missing_roll'].includes(issue.type)
      ));
      if (blockingPaperRollIssues.length > 0) {
        setSaveState('error');
        if (!silent) {
          toast.error(`No se puede guardar. ${blockingPaperRollIssues.map(formatPaperRollIssue).join(' ')}`, { duration: 9000 });
        }
        return false;
      }

      const materialIssues = validateMaterialTraceability(materials, rows);
      if (activeHeader.status === 'closed' && materialIssues.length > 0) {
        setSaveState('error');
        if (!silent) toast.error(`No se puede cerrar. ${materialIssues[0].message}`, { duration: 9000 });
        return false;
      }

      const quantity = computeProducedQuantity(rows);
      const conversionHeader = {
        week: effectiveWeek ?? null,
        total_time_min: activeHeader.total_time_min != null && activeHeader.total_time_min !== '' ? parseInt(activeHeader.total_time_min, 10) : 0,
        adjuster_id: activeHeader.adjuster_id ? parseInt(activeHeader.adjuster_id, 10) : null,
        adjuster_employee_number: activeHeader.adjuster_employee_number || null,
        machinist_id: activeHeader.machinist_id ? parseInt(activeHeader.machinist_id, 10) : null,
        machinist_employee_number: activeHeader.machinist_employee_number || null,
        receiver_id: activeHeader.receiver_id ? parseInt(activeHeader.receiver_id, 10) : null,
        receiver_employee_number: activeHeader.receiver_employee_number || null,
        group_leader_id: activeHeader.group_leader_id ? parseInt(activeHeader.group_leader_id, 10) : null,
        group_leader_employee_number: activeHeader.group_leader_employee_number || null,
        status: activeHeader.status || 'open',
        closed_at: activeHeader.closed_at || null,
        closed_by: activeHeader.closed_by || null,
        created_by: activeHeader.created_by || currentUserEmployeeNumber || null,
      };

      const record = {
        site_id: siteId,
        date: activeHeader.date,
        line_id: parseInt(activeHeader.line_id, 10),
        shift_id: parseInt(activeHeader.shift_id, 10),
        product_id: null,
        quantity,
        conversion_header: conversionHeader,
        rows: normalizeArray(rows).map(uppercaseConversionCapture),
        downtime: normalizeArray(downtime).map(uppercaseConversionCapture),
        material_traceability: normalizeMaterialTraceability(materials),
      };

      setSaving(true);
      setSaveState(isOffline ? 'pending' : 'saving');
      try {
        if (isOffline) {
          const { key } = upsertLocalConversionReport(record, 'pending');
          enqueueConversionReportSync(key);
          refreshPendingCount();
          setLastSavedAt(new Date().toLocaleString());
          if (!silent) toast.success('Guardado local (pendiente de sincronizar)');
          return true;
        }

        const { data } = await axios.post('/production', record);
        upsertLocalConversionReport(data, 'synced');
        refreshPendingCount();
        setLastSavedAt(new Date().toLocaleString());
        setSaveState('saved');
        if (!silent) toast.success('Reporte guardado');
        fetchReportsForDate();
        setIsOffline(false);
        return true;
      } catch (err) {
        conversionLog('Error saving report', {
          status: err?.response?.status,
          message: err?.message,
        });
        if (err.response?.status === 401) {
          navigate('/login');
          return false;
        }
        if (!err.response) {
          setIsOffline(true);
          const { key } = upsertLocalConversionReport(record, 'pending');
          enqueueConversionReportSync(key);
          refreshPendingCount();
          setLastSavedAt(new Date().toLocaleString());
          setSaveState('pending');
          if (!silent) toast.success('Sin conexión: guardado local (pendiente)');
          return true;
        }
        setSaveState('error');
        const detail = err.response?.data?.detail || err.response?.data?.message || 'Error al guardar';
        if (!silent) toast.error(detail);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [downtime, effectiveWeek, fetchReportsForDate, header, isOffline, materials, navigate, refreshPendingCount, rows, siteId]
  );

  const syncPending = useCallback(async () => {
    if (syncing) return;
    if (isOffline) return;

    const queue = loadConversionReportQueue();
    if (!Array.isArray(queue) || queue.length === 0) return;

    setSyncing(true);
    try {
      for (const key of [...queue]) {
        const records = loadConversionReportRecords();
        const rec = records[key];

        if (!rec) {
          dequeueConversionReportSync(key);
          continue;
        }

        if (rec?._local?.status !== 'pending') {
          dequeueConversionReportSync(key);
          continue;
        }

        const payload = {
          site_id: rec.site_id || siteId || undefined,
          date: rec.date,
          line_id: rec.line_id,
          shift_id: rec.shift_id,
          product_id: null,
          quantity: toInt(rec.quantity),
          conversion_header: rec.conversion_header || {
            week: rec.week ?? null,
            total_time_min: rec.total_time_min ?? 0,
            adjuster_id: rec.adjuster_id ?? null,
            adjuster_employee_number: rec.adjuster_employee_number ?? null,
            machinist_id: rec.machinist_id ?? null,
            machinist_employee_number: rec.machinist_employee_number ?? null,
            receiver_id: rec.receiver_id ?? null,
            receiver_employee_number: rec.receiver_employee_number ?? null,
            group_leader_id: rec.group_leader_id ?? null,
            group_leader_employee_number: rec.group_leader_employee_number ?? null,
            status: rec.status || 'open',
            closed_at: rec.closed_at ?? null,
            closed_by: rec.closed_by ?? null,
            created_by: rec.created_by ?? null,
          },
          rows: normalizeArray(rec.rows),
          downtime: normalizeArray(rec.downtime?.length ? rec.downtime : rec.downtimes),
          // Older queued reports omit materials; the API preserves the saved entries.
          ...(Array.isArray(rec.material_traceability) ? { material_traceability: normalizeMaterialTraceability(rec.material_traceability) } : {}),
        };

        try {
          const { data } = await axios.post('/production', payload);
          upsertLocalConversionReport(data, 'synced');
          dequeueConversionReportSync(key);
          refreshPendingCount();
        } catch (err) {
          if (err.response?.status === 401) {
            navigate('/login');
            return;
          }
          if (!err.response) {
            setIsOffline(true);
            break;
          }
          upsertLocalConversionReport(rec, 'error', {
            lastError: err.response?.data?.detail || err.message,
          });
          dequeueConversionReportSync(key);
          refreshPendingCount();
        }
      }

      setLastSyncAt(new Date().toLocaleString());
      fetchReportsForDate();
    } finally {
      setSyncing(false);
    }
  }, [fetchReportsForDate, isOffline, navigate, refreshPendingCount, siteId, syncing]);

  const missingForClose = useMemo(() => {
    const missing = [];
    if (!header.date) missing.push('Fecha');
    if (!header.shift_id) missing.push('Turno');
    if (!header.line_id) missing.push('Máquina');
    if (!resolveEmployeeNumber(header, filteredPeople, 'adjuster')) missing.push('Ajustador');
    if (!resolveEmployeeNumber(header, filteredPeople, 'machinist')) missing.push('Maquinista');
    if (!resolveEmployeeNumber(header, filteredPeople, 'receiver')) missing.push('Recibidor');
    if (!resolveEmployeeNumber(header, filteredPeople, 'group_leader')) missing.push('Jefe de grupo');
    return missing;
  }, [filteredPeople, header]);

  const workOrderValidation = useMemo(() => validateConversionWorkOrders(rows), [rows]);
  const paperRollValidationForClose = useMemo(() => validatePaperRollSeries(rows, { requireAllRows: true }), [rows]);
  const downtimeValidationForClose = useMemo(
    () => validateConversionDowntime(downtime, rows),
    [downtime, rows]
  );

  const onCloseShift = async () => {
    if (!canCloseShift) return;
    if (readOnly) return;

    if (missingForClose.length > 0) {
      toast.error(`Faltan ${missingForClose.length} campos para cerrar: ${missingForClose.join(', ')}`);
      return;
    }

    if (!workOrderValidation.isValid) {
      const details = [];
      if (workOrderValidation.incompleteItemRows.length > 0) {
        details.push(`hay renglón(es) con Item pero sin datos de producción: ${workOrderValidation.incompleteItemRows.join(', ')}`);
      }
      if (workOrderValidation.missingWorkOrderRows.length > 0) {
        details.push(`falta Work Order en renglón(es) ${workOrderValidation.missingWorkOrderRows.join(', ')}`);
      }
      details.push(...workOrderValidation.workOrderIssues.map(formatWorkOrderIssue));
      toast.error(`No se puede cerrar el turno. ${details.join(' | ')}`, { duration: 9000 });
      return;
    }

    if (!paperRollValidationForClose.isValid) {
      toast.error(
        `No se puede cerrar el turno. ${paperRollValidationForClose.issues.map(formatPaperRollIssue).join(' ')}`,
        { duration: 9000 }
      );
      return;
    }

    if (!downtimeValidationForClose.isValid) {
      toast.error(
        `No se puede cerrar el turno. ${formatConversionDowntimeValidation(downtimeValidationForClose).join(' ')}`,
        { duration: 9000 }
      );
      return;
    }

    const materialIssues = validateMaterialTraceability(materials, rows);
    if (materialIssues.length > 0) {
      toast.error(`No se puede cerrar el turno. ${materialIssues[0].message}`, { duration: 9000 });
      return;
    }

    const ok = window.confirm('¿Cerrar turno? Esto dejará el reporte en solo lectura.');
    if (!ok) return;

    const next = {
      ...header,
      status: 'closed',
      closed_at: new Date().toISOString(),
      closed_by: user?.username || user?.email || 'usuario',
    };
    
    const saved = await saveNow({ silent: false, headerOverride: next });
    if (saved) {
      setHeader(next);
      toast.success('Turno cerrado correctamente');
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const pdf = new jsPDF('l', 'pt', 'a3');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 28;
      let y = 36;

      const personName = (id, employeeNumber) => {
        if (employeeNumber) {
          const byNumber = people.find((person) => String(person.employee_number) === String(employeeNumber));
          return byNumber ? `${byNumber.employee_number} - ${byNumber.name}` : String(employeeNumber);
        }
        if (!id) return '—';
        const p = people.find((pp) => String(pp.id) === String(id));
        return p ? `${p.employee_number || ''} - ${p.name || ''}`.trim() : String(id);
      };

      const intOrNull = (v) => {
        if (v === '' || v == null) return null;
        const n = parseInt(String(v), 10);
        return Number.isFinite(n) ? n : null;
      };
      const fmtInt = (v) => {
        const n = intOrNull(v);
        return n == null ? '' : n.toLocaleString('en-US', { maximumFractionDigits: 0 });
      };

      // --- Title ---
      addNovolexLogo(pdf, { x: margin, y: 12, width: 76 });
      pdf.setFontSize(18);
      pdf.setFont(undefined, 'bold');
      const lineCode = selectedLine?.code || header.line_id || '';
      const titleText = lineCode ? `Reporte Diario (Conversión) - ${lineCode}` : 'Reporte Diario (Conversión)';
      pdf.text(titleText, margin + 92, y);
      const siteCode = currentSite?.code || 'SITE';
      const siteName = currentSite?.name || '';
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      pdf.text(`${siteCode}${siteName ? ' - ' + siteName : ''}`, pageWidth - margin, y, { align: 'right' });
      y += 22;

      pdf.setDrawColor(180);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 14;

      // --- Header info (two rows) ---
      const hdrFontSize = 11;
      pdf.setFontSize(hdrFontSize);

      const lineName = selectedLine ? `${selectedLine.code} - ${selectedLine.name}` : (header.line_id || '—');
      const shiftName = selectedShift ? `${selectedShift.code} - ${selectedShift.name}` : (header.shift_id || '—');

      const hdrRow1 = [
        ['Fecha', header.date || '—'],
        ['Turno', shiftName],
        ['Máquina', lineName],
        ['Semana', effectiveWeek != null ? String(effectiveWeek) : '—'],
        ['Estado', readOnly ? 'Cerrado' : 'Abierto'],
      ];
      const hdrRow2 = [
        ['Ajustador', personName(header.adjuster_id, resolveEmployeeNumber(header, filteredPeople, 'adjuster'))],
        ['Maquinista', personName(header.machinist_id, resolveEmployeeNumber(header, filteredPeople, 'machinist'))],
        ['Recibidor', personName(header.receiver_id, resolveEmployeeNumber(header, filteredPeople, 'receiver'))],
        ['Jefe de grupo', personName(header.group_leader_id, resolveEmployeeNumber(header, filteredPeople, 'group_leader'))],
      ];

      const drawHeaderRow = (items, yPos) => {
        const colW = (pageWidth - margin * 2) / items.length;
        items.forEach(([label, value], i) => {
          const x = margin + i * colW;
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(100);
          pdf.text(label + ':', x, yPos);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(0);
          pdf.text(String(value), x + pdf.getTextWidth(label + ': '), yPos);
        });
      };

      drawHeaderRow(hdrRow1, y);
      y += 14;
      drawHeaderRow(hdrRow2, y);
      y += 6;

      if (readOnly && header.closed_by) {
        y += 10;
        pdf.setFontSize(10);
        pdf.setTextColor(100);
        pdf.text(`Cerrado por: ${header.closed_by}${header.closed_at ? '  ·  ' + new Date(header.closed_at).toLocaleString() : ''}`, margin, y);
        pdf.setTextColor(0);
      }
      y += 10;

      // --- Production table ---
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Producción', margin, y);
      y += 4;

      const itemMatches = itemValuesMatch;

      const listRows = Array.isArray(rows) ? rows : [];
      const orderDataByItem = new Map();
      const uniqueItems = Array.from(new Set(
        listRows
          .map((r) => String(r?.item || '').trim())
          .filter(Boolean)
      ));

      for (const item of uniqueItems) {
        const params = { search: item, per_page: 25, page: 1 };
        if (siteId) params.site_id = siteId;
        try {
          const { data } = await axios.get('/orders-data', { params });
          const options = Array.isArray(data?.data) ? data.data : [];
          const exact = options.find((o) => itemMatches(o?.item, item));
          if (exact) {
            orderDataByItem.set(item, exact);
          }
        } catch (e) {
          // continue: export still works with local metadata fallback
        }
      }

      const prodRows = listRows.map((r, idx) => {
        const meta = r.details || {};
        const itemKey = String(r.item || '').trim();
        const orderMeta = itemKey ? orderDataByItem.get(itemKey) : null;
        const productMeta = itemKey ? productsByItem.get(itemKey) : null;
        const resolvedName = meta.name || orderMeta?.name || productMeta?.name || '';
        const resolvedBagStyle = meta.bag_style || orderMeta?.bag_style || productMeta?.bag_style || '';
        const resolvedBagSize = meta.bag_size_group || orderMeta?.bag_size_group || productMeta?.bag_size_group || '';
        const resolvedPacking = meta.packing_group || orderMeta?.packing_group || productMeta?.packing_group || '';
        return [
          idx + 1,
          r.item || '',
          resolvedName,
          resolvedBagStyle,
          resolvedBagSize,
          resolvedPacking,
          r.work_order || '',
          r.pallet_no || '',
          r.roll_no || '',
          fmtInt(r.quantity),
          fmtInt(r.counter),
          fmtInt(r.bags_good),
          fmtInt(r.scrap),
          r.serial_or_part || '',
          r.fsc ? 'Si' : '',
        ];
      });

      const prodTotals = {
        quantity: listRows.reduce((a, r) => a + toInt(r.quantity), 0),
        counter: listRows.reduce((a, r) => a + toInt(r.counter), 0),
        good: listRows.reduce((a, r) => a + toInt(r.bags_good), 0),
        scrap: listRows.reduce((a, r) => a + toInt(r.scrap), 0),
      };
      prodRows.push(['', '', '', '', '', '', 'TOTALES', '', '', fmtInt(prodTotals.quantity), fmtInt(prodTotals.counter), fmtInt(prodTotals.good), fmtInt(prodTotals.scrap), '', '']);

      autoTable(pdf, {
        startY: y,
        margin: { left: margin, right: margin },
        tableWidth: pageWidth - margin * 2,
        head: [['#', 'Item', 'Nombre', 'Bag Style', 'Bag Size', 'Packing', 'Work Order', '#Tarima', '#Rollo', 'Cantidad', 'Contador', 'Bolsas buenas', 'Scrap', 'Serie de Papel', 'FSC']],
        body: prodRows,
        styles: { fontSize: 9, cellPadding: 2.5, overflow: 'linebreak' },
        headStyles: { fillColor: [41, 65, 122], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 18, halign: 'center' },
          1: { cellWidth: 60 },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 60 },
          4: { cellWidth: 100 },
          5: { cellWidth: 40 },
          6: { cellWidth: 60 },
          7: { cellWidth: 45, halign: 'center' },
          8: { cellWidth: 45, halign: 'center' },
          9: { cellWidth: 45, halign: 'right' },
          10: { cellWidth: 45, halign: 'right' },
          11: { cellWidth: 60, halign: 'right' },
          12: { cellWidth: 50, halign: 'right' },
          13: { cellWidth: 60 },
          14: { cellWidth: 25, halign: 'center' },
        },
        didParseCell: (data) => {
          // Style totals row (last row)
          if (data.section === 'body' && data.row.index === prodRows.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [235, 238, 245];
          }
        },
        theme: 'grid',
      });

      y = pdf.lastAutoTable.finalY + 14;

      // --- Downtime table ---
      const dtList = Array.isArray(downtime) ? downtime : [];
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Mano de Obra A/X (Tiempo muerto)', margin, y);
      y += 4;

      if (dtList.length === 0) {
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        y += 10;
        pdf.text('Sin eventos de tiempo muerto.', margin, y);
        y += 14;
      } else {
        const dtRows = dtList.map((e) => {
          const min = toInt(e.minutes);
          const hrs = min > 0 ? (min / 60).toFixed(2) : '0.00';
          const codeObj = (Array.isArray(downtimeCodes) ? downtimeCodes : []).find((c) => String(c.code) === String(e.code));
          const codeLabel = codeObj ? `${codeObj.code} - ${codeObj.name}` : (e.code || '');
          return [e.work_order || '', codeLabel, min || '', hrs, toInt(e.quantity) || '', toInt(e.scrap) || '', e.notes || ''];
        });

        const totalDtMin = dtList.reduce((a, e) => a + toInt(e.minutes), 0);
        const totalDtQty = dtList.reduce((a, e) => a + toInt(e.quantity), 0);
        const totalDtScrap = dtList.reduce((a, e) => a + toInt(e.scrap), 0);
        dtRows.push(['', 'TOTALES', totalDtMin || '', (totalDtMin / 60).toFixed(2), fmtInt(totalDtQty), fmtInt(totalDtScrap), '']);

        const dtTableWidth = (pageWidth - margin * 2) * 0.75;

        autoTable(pdf, {
          startY: y,
          margin: { left: margin },
          tableWidth: dtTableWidth,
          head: [['Work Order', 'Código T muerto', 'Minutos', 'Horas', 'Cantidad', 'Scrap', 'Notas']],
          body: dtRows,
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
          headStyles: { fillColor: [71, 85, 105], textColor: 255, fontStyle: 'bold', fontSize: 9 },
          columnStyles: {
            0: { cellWidth: 80 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 45, halign: 'right' },
            3: { cellWidth: 45, halign: 'right' },
            4: { cellWidth: 50, halign: 'right' },
            5: { cellWidth: 45, halign: 'right' },
            6: { cellWidth: 130 },
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.row.index === dtRows.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [235, 238, 245];
            }
          },
          theme: 'grid',
        });
        y = pdf.lastAutoTable.finalY + 14;
      }

      if (materials.length > 0) {
        if (y > pdf.internal.pageSize.getHeight() - 90) {
          pdf.addPage();
          y = margin;
        }
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Trazabilidad de materiales', margin, y);
        autoTable(pdf, {
          startY: y + 4,
          margin: { left: margin, right: margin },
          head: [['Work Order', 'Material', 'P.O. / lote', 'Tarimas']],
          body: materials.map((material) => [
            material.work_order, materialLabel(material.material_type),
            material.reference || 'Pendiente', materialPalletLabel(material),
          ]),
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: [71, 85, 105] },
          theme: 'grid',
        });
        y = pdf.lastAutoTable.finalY + 14;
      }

      // --- KPIs ---
      if (y > pdf.internal.pageSize.getHeight() - 90) {
        pdf.addPage();
        y = margin;
      }
      const evtList = Array.isArray(downtime) ? downtime : [];
      const downTimeMin = evtList.reduce((a, e) => a + (e.code === '101' ? 0 : toInt(e.minutes)), 0);
      const runtimeFromCode = evtList.reduce((a, e) => a + (e.code === '101' ? toInt(e.minutes) : 0), 0);
      const headerTotalTimeMin = Math.max(0, toInt(header?.total_time_min));
      const runTimeMin = runtimeFromCode > 0
        ? runtimeFromCode
        : headerTotalTimeMin > 0
          ? Math.max(0, headerTotalTimeMin - downTimeMin)
          : 0;
      const totalTimeMin = downTimeMin + runTimeMin;
      const uptime = totalTimeMin > 0 ? runTimeMin / totalTimeMin : 0;
      const dtPct = totalTimeMin > 0 ? downTimeMin / totalTimeMin : 0;

      const rList = Array.isArray(rows) ? rows : [];
      const bagsGoodRaw = rList.reduce((a, r) => a + toInt(r.bags_good), 0);
      const counterRaw = rList.reduce((a, r) => a + toInt(r.counter), 0);
      const quantityRaw = rList.reduce((a, r) => a + toInt(r.quantity), 0);
      const scrapTotal = rList.reduce((a, r) => a + toInt(r.scrap), 0);
      const bagsGood = bagsGoodRaw > 0 ? bagsGoodRaw : counterRaw > 0 ? counterRaw : quantityRaw;
      const quality = counterRaw > 0 ? bagsGoodRaw / counterRaw : 0;
      const bpm = runTimeMin > 0 ? bagsGood / runTimeMin : 0;
      const oeeLite = uptime * quality;
      const useGoodAsSummaryBasis = bagsGoodRaw > 0;
      const summaryQtyOfRow = (r) => {
        if (useGoodAsSummaryBasis) return toInt(r?.bags_good);
        const counter = toInt(r?.counter);
        if (counter > 0) return counter;
        return toInt(r?.quantity);
      };

      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('KPIs', margin, y);
      y += 4;

      autoTable(pdf, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Total Time (hrs)', 'Down Time (hrs)', 'Run Time (hrs)', 'Uptime %', '% DT', 'BPM', 'Quality', 'OEE-lite', 'Bolsas buenas', 'Scrap']],
        body: [[
          `${(totalTimeMin / 60).toFixed(2)} hrs`,
          `${(downTimeMin / 60).toFixed(2)} hrs`,
          `${(runTimeMin / 60).toFixed(2)} hrs`,
          `${(uptime * 100).toFixed(1)}%`,
          `${(dtPct * 100).toFixed(1)}%`,
          bpm.toFixed(2),
          `${(quality * 100).toFixed(1)}%`,
          `${(oeeLite * 100).toFixed(1)}%`,
          bagsGood.toLocaleString(),
          scrapTotal.toLocaleString(),
        ]],
        styles: { fontSize: 10, cellPadding: 4, halign: 'center' },
        headStyles: { fillColor: [22, 101, 52], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        theme: 'grid',
      });
      y = pdf.lastAutoTable.finalY + 12;

      // --- Summary tables side by side ---
      const groupSum = (arr, keyFn, valueFn) => {
        const m = new Map();
        (arr || []).forEach((r) => { const k = keyFn(r); if (!k) return; m.set(k, (m.get(k) || 0) + toInt(valueFn(r))); });
        return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
      };

      const getResolvedMeta = (row) => {
        const meta = row?.details || {};
        const itemKey = String(row?.item || '').trim();

        let orderMeta = itemKey ? orderDataByItem.get(itemKey) : null;
        if (!orderMeta && itemKey) {
          const found = Array.from(orderDataByItem.entries()).find(([k]) => itemMatches(k, itemKey));
          if (found) orderMeta = found[1];
        }

        let productMeta = itemKey ? productsByItem.get(itemKey) : null;
        if (!productMeta && itemKey) {
          const found = Array.from(productsByItem.entries()).find(([k]) => itemMatches(k, itemKey));
          if (found) productMeta = found[1];
        }

        return {
          bag_size_group: meta.bag_size_group || orderMeta?.bag_size_group || productMeta?.bag_size_group || '',
          bag_style: meta.bag_style || orderMeta?.bag_style || productMeta?.bag_style || '',
          packing_group: meta.packing_group || orderMeta?.packing_group || productMeta?.packing_group || '',
        };
      };

      const woSummary = groupSum(rList, (r) => (r.work_order || '').trim(), (r) => summaryQtyOfRow(r));
      const sizeSummary = groupSum(rList, (r) => getResolvedMeta(r).bag_size_group, (r) => summaryQtyOfRow(r));
      const styleSummary = groupSum(rList, (r) => getResolvedMeta(r).bag_style, (r) => summaryQtyOfRow(r));
      const packingSummary = groupSum(rList, (r) => getResolvedMeta(r).packing_group, (r) => summaryQtyOfRow(r));

      const summaries = [
        { title: 'Por Work Order', heads: ['Work Order', 'Cantidad', 'Horas*'], data: woSummary.map(([k, v]) => [k, v.toLocaleString(), bagsGood > 0 ? ((runTimeMin * (v / bagsGood)) / 60).toFixed(2) : '0.00']) },
        { title: 'Por Bag Size', heads: ['Bag Size', 'Cantidad'], data: sizeSummary.map(([k, v]) => [k, v.toLocaleString()]) },
        { title: 'Por Bag Style', heads: ['Bag Style', 'Cantidad'], data: styleSummary.map(([k, v]) => [k, v.toLocaleString()]) },
        { title: 'Por Packing', heads: ['Packing', 'Cantidad'], data: packingSummary.map(([k, v]) => [k, v.toLocaleString()]) },
      ];

      const colCount = summaries.length;
      const colWidth = (pageWidth - margin * 2 - (colCount - 1) * 10) / colCount;

      summaries.forEach((s, idx) => {
        const xStart = margin + idx * (colWidth + 10);
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(50);
        pdf.text(s.title, xStart, y + 10);

        if (s.data.length === 0) {
          pdf.setFontSize(9);
          pdf.setFont(undefined, 'normal');
          pdf.text('—', xStart, y + 22);
        } else {
          autoTable(pdf, {
            startY: y + 14,
            margin: { left: xStart, right: pageWidth - xStart - colWidth },
            tableWidth: colWidth,
            head: [s.heads],
            body: s.data.slice(0, 15),
            styles: { fontSize: 8.5, cellPadding: 2.5, overflow: 'linebreak' },
            headStyles: { fillColor: [100, 116, 139], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
            theme: 'grid',
          });
        }
      });

      pdf.setTextColor(0);

      const shiftLabel = selectedShift?.code || header.shift_id || 'Turno';
      const lineLabel = selectedLine?.code || header.line_id || 'Linea';
      const dateLabel = header.date || 'fecha';
      const fileName = `${shiftLabel}-${lineLabel}-${dateLabel}.pdf`;
      pdf.save(fileName);
    } finally {
      setExporting(false);
    }
  };

  const onHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader((prev) => ({ ...prev, [name]: value }));
  };

  // Purge stale synced local records on mount (keeps pending ones)
  useEffect(() => {
    purgeStaleLocalRecords();
  }, []);

  // Auth bootstrap
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
    }
  }, [authLoading, navigate, user]);

  // Load site-scoped catalogs once after authentication and whenever the site changes.
  useEffect(() => {
    if (authLoading || siteLoading || !user || !siteId) return;
    fetchCatalogs();
    fetchPeople();
  }, [authLoading, fetchCatalogs, fetchPeople, siteId, siteLoading, user]);

  // Online/offline events
  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Employees can be added from another screen while this report remains open.
  useEffect(() => {
    if (authLoading || !isAuthenticated()) return undefined;

    const refreshVisibleEmployees = () => {
      if (document.visibilityState === 'visible') fetchPeople();
    };

    window.addEventListener('focus', refreshVisibleEmployees);
    document.addEventListener('visibilitychange', refreshVisibleEmployees);
    return () => {
      window.removeEventListener('focus', refreshVisibleEmployees);
      document.removeEventListener('visibilitychange', refreshVisibleEmployees);
    };
  }, [authLoading, fetchPeople, isAuthenticated]);

  // Restore last selected report for this user (per browser)
  useEffect(() => {
    if (authLoading) return;
    if (!user?.id) return;
    if (!siteId) return;

    const saved = readJson(lastReportKey(user.id), null);
    if (!saved) return;
    if (saved.site_id && String(saved.site_id) !== String(siteId)) return;

    if (saved.line_id && saved.shift_id) {
      setHeader((prev) => ({
        ...prev,
        // Always use today's business date, only restore line/shift selection
        line_id: String(saved.line_id),
        shift_id: String(saved.shift_id),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, siteId, user?.id]);

  // Clear a previously saved selection when it is not a Conversion R## machine.
  useEffect(() => {
    if (!header.line_id || lines.length === 0) return;
    const isAllowed = lines.some((line) => String(line.id) === String(header.line_id));
    if (!isAllowed) {
      setHeader((prev) => ({ ...prev, line_id: '' }));
    }
  }, [header.line_id, lines]);

  // Persist last selection
  useEffect(() => {
    if (!user?.id) return;
    if (!siteId || !header.date || !header.line_id || !header.shift_id) return;
    writeJson(lastReportKey(user.id), {
      site_id: siteId,
      date: header.date,
      line_id: header.line_id,
      shift_id: header.shift_id
    });
  }, [header.date, header.line_id, header.shift_id, siteId, user?.id]);

  // Load report when identity changes
  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  // Refresh report list when date changes
  useEffect(() => {
    if (authLoading || siteLoading || !user) return;
    fetchReportsForDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, header.date, siteId, siteLoading, user]);

  useConversionReportListRefresh(
    reportViewTab === 'others' && !authLoading && !siteLoading && !!user && !!siteId && !!header.date && !isOffline,
    fetchReportsForDate
  );

  // Auto-save (debounced)
  useEffect(() => {
    if (readOnly) return;
    if (!siteId || !header.date || !header.line_id || !header.shift_id) return;
    if (hydratingRef.current) return;
    if (skipAutosaveOnceRef.current) {
      skipAutosaveOnceRef.current = false;
      return;
    }

    setSaveState((prev) => (prev === 'saving' ? prev : 'dirty'));

    const t = setTimeout(() => {
      saveNow({ silent: true });
    }, AUTO_SAVE_DELAY_MS);

    return () => clearTimeout(t);
  }, [downtime, header, materials, readOnly, rows, saveNow, siteId]);

  // Auto sync when back online
  useEffect(() => {
    refreshPendingCount();
    if (!isOffline) {
      syncPending();
    }
  }, [isOffline, refreshPendingCount, syncPending]);

  // When marked offline due to API failure (but browser still online), probe backend periodically
  // so the "Offline" label can recover automatically without requiring a network change event.
  useEffect(() => {
    if (!isOffline) return;
    if (authLoading) return;
    if (!isAuthenticated()) return;

    const probe = () => {
      fetchReportsForDate({ forceRemote: true });
    };

    probe();
    const t = setInterval(probe, 10000);
    return () => clearInterval(t);
  }, [authLoading, fetchReportsForDate, isAuthenticated, isOffline]);

  const canCapture = Boolean(siteId && header.date && header.line_id && header.shift_id);
  const missingCaptureFields = [
    !siteId ? 'Empresa/Sitio' : null,
    !header.date ? 'Fecha' : null,
    !header.shift_id ? 'Turno' : null,
    !header.line_id ? 'Máquina' : null,
  ].filter(Boolean);
  const captureBlockMessage = missingCaptureFields.length > 0
    ? `Falta: ${missingCaptureFields.join(', ')}.`
    : 'Selecciona Fecha, Turno y Máquina para empezar.';

  const reportSelectorValue = useMemo(() => {
    if (!siteId || !header.date || !header.line_id || !header.shift_id) return '';
    return makeConversionReportKey({ site_id: siteId, date: header.date, line_id: header.line_id, shift_id: header.shift_id });
  }, [header.date, header.line_id, header.shift_id, siteId]);

  const selectedReport = useMemo(() => {
    if (!reportSelectorValue) return null;
    return availableReports.find((r) => makeConversionReportKey({ site_id: siteId, date: r.date, line_id: r.line_id, shift_id: r.shift_id }) === reportSelectorValue) || null;
  }, [availableReports, reportSelectorValue, siteId]);

  const selectedReportOwnerEmployeeNumber = selectedReport ? getReportOwnerEmployeeNumber(selectedReport) : null;
  const isSelectedReportOwnedByCurrentUser = Boolean(
    currentUserEmployeeNumber
    && selectedReportOwnerEmployeeNumber
    && String(selectedReportOwnerEmployeeNumber) === String(currentUserEmployeeNumber)
  );
  const isSelectedReportActive = Boolean(selectedReport && selectedReport.status !== 'closed');
  const canDeleteAnyReport = can('conversion:deleteReport');
  const canDeleteSelectedReport = Boolean(
    selectedReport
    && (canDeleteAnyReport || (isSelectedReportOwnedByCurrentUser && isSelectedReportActive))
  );
  const deleteRestrictionMessage = !selectedReport
    ? 'Selecciona un badge/reporte para borrar'
    : canDeleteAnyReport
      ? 'Borrar reporte y toda su información'
      : !isSelectedReportOwnedByCurrentUser
        ? 'Solo puedes borrar tus propios reportes activos'
        : !isSelectedReportActive
          ? 'Solo puedes borrar reportes activos (abiertos)'
          : 'Borrar reporte activo y toda su información';

  const selectedReportMeta = useMemo(() => {
    if (!selectedReport) return null;
    const line = lines.find((l) => String(l.id) === String(selectedReport.line_id));
    const shift = shifts.find((s) => String(s.id) === String(selectedReport.shift_id));
    return {
      lineLabel: line?.code || `Línea ${selectedReport.line_id}`,
      shiftLabel: shift?.code || `Turno ${selectedReport.shift_id}`,
      dateLabel: selectedReport?.date || header.date || 'sin fecha',
    };
  }, [header.date, lines, selectedReport, shifts]);

  const canReopenReports = can('conversion:reopen');

  const requestReopenSelectedReport = useCallback(() => {
    if (!selectedReport?.id || selectedReport.status !== 'closed') {
      toast.error('Selecciona un reporte cerrado para reabrir');
      return;
    }
    if (!canReopenReports) {
      toast.error('No tienes permiso para reabrir reportes');
      return;
    }
    if (isOffline) {
      toast.error('Se necesita conexión para reabrir un reporte');
      return;
    }
    setReopenReason('');
    setShowReopenModal(true);
  }, [canReopenReports, isOffline, selectedReport]);

  const reopenSelectedReport = useCallback(async () => {
    const reason = reopenReason.trim();
    if (!selectedReport?.id || selectedReport.status !== 'closed') {
      toast.error('El reporte seleccionado no está cerrado');
      return;
    }
    if (!canReopenReports) {
      toast.error('No tienes permiso para reabrir reportes');
      return;
    }
    if (reason.length < 3) {
      toast.error('Escribe un motivo de al menos 3 caracteres');
      return;
    }

    setReopeningReport(true);
    try {
      const { data } = await axios.post(`/production/${selectedReport.id}/reopen`, { reason });
      const report = data?.report;
      if (report) {
        applyLoadedRecord(report);
        upsertLocalConversionReport(report, 'synced');
      } else {
        setHeader((prev) => ({ ...prev, status: 'open' }));
      }
      setAvailableReports((prev) => prev.map((item) => (
        item.id === selectedReport.id ? { ...item, status: 'open' } : item
      )));
      setShowReopenModal(false);
      setReopenReason('');
      fetchReportsForDate();
      toast.success('Reporte reabierto. Ya puede editarse.');
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || 'No fue posible reabrir el reporte';
      toast.error(detail);
    } finally {
      setReopeningReport(false);
    }
  }, [applyLoadedRecord, canReopenReports, fetchReportsForDate, reopenReason, selectedReport]);

  const requestDeleteSelectedReport = useCallback(() => {
    if (!selectedReport) {
      toast.error('No hay un reporte activo para borrar');
      return;
    }
    if (!canDeleteSelectedReport) {
      toast.error('Solo puedes borrar tus reportes activos');
      return;
    }
    const code = String(Math.floor(1000 + Math.random() * 9000));
    setDeleteConfirmCode(code);
    setDeleteCodeInput('');
    setShowDeleteReportModal(true);
  }, [canDeleteSelectedReport, selectedReport]);

  const deleteSelectedReport = useCallback(async () => {
    if (!selectedReport) {
      toast.error('No hay un reporte activo para borrar');
      return;
    }
    if (!canDeleteSelectedReport) {
      toast.error('Solo puedes borrar tus reportes activos');
      return;
    }

    if (!deleteConfirmCode || String(deleteCodeInput).trim() !== String(deleteConfirmCode)) {
      toast.error('Código de confirmación incorrecto');
      return;
    }

    const key = makeConversionReportKey({
      site_id: siteId,
      date: selectedReport.date,
      line_id: selectedReport.line_id,
      shift_id: selectedReport.shift_id,
    });

    setDeletingReport(true);
    try {
      if (selectedReport.id) {
        if (isOffline) {
          toast.error('Sin conexión: no se puede borrar un reporte ya sincronizado');
          return;
        }
        await axios.delete(`/production/${selectedReport.id}`);
      }

      removeLocalConversionReport(key);
      refreshPendingCount();
      setAvailableReports((prev) => prev.filter((r) => makeConversionReportKey({ site_id: siteId, date: r.date, line_id: r.line_id, shift_id: r.shift_id }) !== key));

      hydratingRef.current = true;
      setRows([]);
      setDowntime([]);
      setMaterials([]);
      setHeader(defaultHeader(getBusinessDateISO()));
      Promise.resolve().then(() => {
        hydratingRef.current = false;
      });

      fetchReportsForDate();
      toast.success('Reporte eliminado correctamente');
      setShowDeleteReportModal(false);
      setDeleteConfirmCode('');
      setDeleteCodeInput('');
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || 'Error al borrar reporte';
      toast.error(detail);
    } finally {
      setDeletingReport(false);
    }
  }, [canDeleteSelectedReport, deleteCodeInput, deleteConfirmCode, fetchReportsForDate, isOffline, refreshPendingCount, selectedReport, siteId]);

  const sortedReports = useMemo(() => {
    return availableReports
      .filter((report) => lines.some((line) => String(line.id) === String(report.line_id)))
      .slice()
      .sort((a, b) => {
        const aOwner = getReportOwnerEmployeeNumber(a);
        const bOwner = getReportOwnerEmployeeNumber(b);
        const aIsCurrentUser = currentUserEmployeeNumber && aOwner && String(aOwner) === String(currentUserEmployeeNumber);
        const bIsCurrentUser = currentUserEmployeeNumber && bOwner && String(bOwner) === String(currentUserEmployeeNumber);
        if (aIsCurrentUser && !bIsCurrentUser) return -1;
        if (!aIsCurrentUser && bIsCurrentUser) return 1;
        if (a?.line_id != null && b?.line_id != null && a.line_id !== b.line_id) return a.line_id - b.line_id;
        if (a?.shift_id != null && b?.shift_id != null) return a.shift_id - b.shift_id;
        return 0;
      });
  }, [availableReports, currentUserEmployeeNumber, getReportOwnerEmployeeNumber, lines]);

  const searchedReports = useMemo(() => {
    const searchLower = reportSearchFilter.trim().toLowerCase();
    if (!searchLower) return sortedReports;
    return sortedReports.filter((r) => {
      const line = lines.find((l) => String(l.id) === String(r.line_id));
      const shift = shifts.find((s) => String(s.id) === String(r.shift_id));
      return (
        (line?.code || '').toLowerCase().includes(searchLower) ||
        (shift?.code || '').toLowerCase().includes(searchLower) ||
        String(r.line_id).includes(searchLower) ||
        String(r.shift_id).includes(searchLower)
      );
    });
  }, [lines, reportSearchFilter, shifts, sortedReports]);

  const mineReportsCount = useMemo(() => {
    if (!currentUserEmployeeNumber) return 0;
    return sortedReports.filter((r) => String(getReportOwnerEmployeeNumber(r) || '') === String(currentUserEmployeeNumber)).length;
  }, [currentUserEmployeeNumber, getReportOwnerEmployeeNumber, sortedReports]);

  const otherReportsCount = useMemo(() => Math.max(0, sortedReports.length - mineReportsCount), [mineReportsCount, sortedReports.length]);

  const visibleReports = useMemo(() => {
    if (reportViewTab === 'mine') {
      if (!currentUserEmployeeNumber) return [];
      return searchedReports.filter((r) => String(getReportOwnerEmployeeNumber(r) || '') === String(currentUserEmployeeNumber));
    }
    return searchedReports.filter((r) => !currentUserEmployeeNumber || String(getReportOwnerEmployeeNumber(r) || '') !== String(currentUserEmployeeNumber));
  }, [currentUserEmployeeNumber, getReportOwnerEmployeeNumber, reportViewTab, searchedReports]);

  const reportsByShift = useMemo(() => {
    const groups = new Map();
    visibleReports.forEach((report) => {
      const shiftId = String(report.shift_id);
      if (!groups.has(shiftId)) {
        const shift = shifts.find((item) => String(item.id) === shiftId);
        groups.set(shiftId, {
          shiftId,
          code: shift?.code || shiftId,
          reports: [],
        });
      }
      groups.get(shiftId).reports.push(report);
    });
    return Array.from(groups.values()).sort((a, b) =>
      String(a.code).localeCompare(String(b.code), 'es', { numeric: true })
    );
  }, [shifts, visibleReports]);

  const clearReportEditor = useCallback(() => {
    hydratingRef.current = true;
    skipAutosaveOnceRef.current = true;
    setHeader(defaultHeader(header.date || defaultBusinessDate));
    setRows([]);
    setDowntime([]);
    setMaterials([]);
    setSaveState('idle');
    setLastSavedAt(null);
    if (user?.id) removeKey(`conversion_last_report_v1:${user.id}`);
    Promise.resolve().then(() => {
      hydratingRef.current = false;
    });
  }, [defaultBusinessDate, header.date, user?.id]);

  const changeReportViewTab = useCallback((nextTab) => {
    if (nextTab === reportViewTab) return;

    const ownerEmployeeNumber = selectedReport
      ? getReportOwnerEmployeeNumber(selectedReport)
      : null;
    const selectedIsMine = Boolean(
      currentUserEmployeeNumber
      && ownerEmployeeNumber
      && String(ownerEmployeeNumber) === String(currentUserEmployeeNumber)
    );
    const selectedBelongsToNextTab = Boolean(
      selectedReport && (nextTab === 'mine' ? selectedIsMine : !selectedIsMine)
    );

    setReportViewTab(nextTab);
    if (!selectedBelongsToNextTab) clearReportEditor();
  }, [clearReportEditor, currentUserEmployeeNumber, getReportOwnerEmployeeNumber, reportViewTab, selectedReport]);

  const selectExistingReport = useCallback((report) => {
    if (!report) return;

    conversionLog('Badge clicked', {
      reportId: report?.id,
      date: report?.date,
      line_id: report?.line_id,
      shift_id: report?.shift_id,
      status: report?.status,
    });

    const nextDate = normalizeDateValue(report?.date) || '';
    const nextLineId = report?.line_id != null ? String(report.line_id) : '';
    const nextShiftId = report?.shift_id != null ? String(report.shift_id) : '';

    setHeader((prev) => {
      const needsUpdate =
        (nextDate && nextDate !== prev.date) ||
        (nextLineId && nextLineId !== String(prev.line_id)) ||
        (nextShiftId && nextShiftId !== String(prev.shift_id));

      if (!needsUpdate) return prev;

      return {
        ...prev,
        date: nextDate || prev.date,
        line_id: nextLineId || prev.line_id,
        shift_id: nextShiftId || prev.shift_id,
      };
    });

    // Si ya vienen detalles en el listado, hidratar de inmediato para evitar pantalla vacía
    if ((Array.isArray(report?.rows) && report.rows.length > 0) || (Array.isArray(report?.downtime) && report.downtime.length > 0) || (Array.isArray(report?.downtimes) && report.downtimes.length > 0) || (Array.isArray(report?.material_traceability) && report.material_traceability.length > 0)) {
      applyLoadedRecord(report);
      conversionLog('Hydrated immediately from report details', { reportId: report?.id });
    }
  }, [applyLoadedRecord]);

  useEffect(() => {
    conversionLog('Capture state', {
      canCapture,
      siteId,
      date: header.date,
      line_id: header.line_id,
      shift_id: header.shift_id,
      missing: missingCaptureFields,
    });
  }, [canCapture, header.date, header.line_id, header.shift_id, missingCaptureFields, siteId]);

  const newReport = () => {
    const ok = window.confirm('¿Crear un nuevo reporte (limpiar renglones y eventos)?');
    if (!ok) return;
    hydratingRef.current = true;
    setRows([]);
    setDowntime([]);
    setMaterials([]);
    // Reset header to default values but keep current business date
    setHeader(defaultHeader(getBusinessDateISO()));
    setReportViewTab('mine');
    Promise.resolve().then(() => {
      hydratingRef.current = false;
    });
    setSaveState('dirty');
  };

  const navigateToOperationalDate = (nextDate) => {
    if (!nextDate || nextDate === header.date) return;
    if (saving) {
      toast.error('Espera a que termine el guardado antes de cambiar de día');
      return;
    }
    if (saveState === 'dirty') {
      const proceed = window.confirm('Hay cambios sin guardar. ¿Cambiar de día y descartarlos?');
      if (!proceed) return;
    }

    hydratingRef.current = true;
    skipAutosaveOnceRef.current = true;
    setHeader(defaultHeader(nextDate));
    setRows([]);
    setDowntime([]);
    setMaterials([]);
    setSaveState('idle');
    setLastSavedAt(null);
    setReportSearchFilter('');
    Promise.resolve().then(() => {
      hydratingRef.current = false;
    });
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-50" onKeyDown={advanceReportFieldOnEnter}>
      {/* Fixed header card */}
      <div className="sticky top-0 z-30 bg-slate-50/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Datos del reporte</div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-extrabold text-slate-900">Reporte Diario (Conversión)</h1>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold border ${readOnly ? 'bg-slate-100 text-slate-700 border-slate-200' : 'bg-green-50 text-green-800 border-green-200'}`}>
                    {readOnly ? 'Cerrado' : 'Abierto'}
                  </span>
                  {readOnly && (
                    <span className="text-sm text-slate-600">
                      Cerrado por <span className="font-semibold">{header.closed_by || '—'}</span>
                      {header.closed_at ? ` · ${new Date(header.closed_at).toLocaleString()}` : ''}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm text-slate-600">
                  Día operativo: <span className="font-semibold">07:00–06:59</span>
                  {saveState === 'dirty' && <span className="ml-2 text-amber-700 font-semibold">Sin guardar</span>}
                  {saveState === 'saving' && <span className="ml-2 text-slate-500 font-semibold">Guardando…</span>}
                  {saveState === 'saved' && <span className="ml-2 text-green-700 font-semibold">Guardado</span>}
                  {saveState === 'pending' && <span className="ml-2 text-amber-700 font-semibold">Guardado (pendiente)</span>}
                  {saveState === 'error' && <span className="ml-2 text-red-700 font-semibold">Error al guardar</span>}
                  {pendingCount > 0 && <span className="ml-2 text-amber-700 font-semibold">Pendientes: {pendingCount}</span>}
                  {syncing && <span className="ml-2 text-slate-500">Sincronizando…</span>}
                  {lastSyncAt && <span className="ml-2 text-slate-500">Última sync: {lastSyncAt}</span>}
                  {lastSavedAt && <span className="ml-2 text-slate-500">Último guardado: {lastSavedAt}</span>}
                  {isOffline && <span className="ml-2 text-amber-700 font-semibold">Offline</span>}
                  {!readOnly && missingForClose.length > 0 && (
                    <span className="ml-2 text-amber-800 font-semibold">Faltan {missingForClose.length} campos para cerrar</span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => saveNow({ silent: false })}
                  disabled={saving || readOnly}
                  className="px-4 py-2 rounded-lg bg-blue-700 text-white font-semibold shadow hover:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
                  title={readOnly ? 'Reporte cerrado' : 'Guardar'}
                >
                  {saving ? 'Guardando…' : saveState === 'pending' ? 'Guardado (pendiente)' : saveState === 'saved' ? 'Guardado' : 'Guardar'}
                </button>
                <button
                  type="button"
                  onClick={syncPending}
                  disabled={isOffline || syncing || pendingCount === 0}
                  className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-800 font-semibold shadow-sm hover:bg-slate-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                  title={isOffline ? 'Sin conexión' : pendingCount === 0 ? 'No hay pendientes' : 'Sincronizar'}
                >
                  Sincronizar
                </button>
                <button
                  type="button"
                  onClick={onCloseShift}
                  disabled={!canCloseShift || readOnly || saving}
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold shadow hover:bg-black disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
                  title={!canCloseShift ? 'No tiene permiso para cerrar' : readOnly ? 'Ya está cerrado' : 'Cerrar turno'}
                >
                  Cerrar turno
                </button>
                {readOnly && canReopenReports && (
                  <button
                    type="button"
                    onClick={requestReopenSelectedReport}
                    disabled={reopeningReport || isOffline || !selectedReport?.id}
                    className="px-4 py-2 rounded-lg bg-amber-500 text-amber-950 font-extrabold shadow hover:bg-amber-400 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
                    title={isOffline ? 'Se necesita conexión' : 'Reabrir reporte cerrado'}
                  >
                    {reopeningReport ? 'Reabriendo…' : 'Reabrir reporte'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={newReport}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-800 font-semibold shadow-sm hover:bg-slate-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  Nuevo
                </button>
                <button
                  type="button"
                  onClick={exportPdf}
                  disabled={exporting}
                  className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-800 font-semibold shadow-sm hover:bg-slate-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  {exporting ? 'Exportando…' : 'Exportar PDF'}
                </button>
              </div>
            </div>

            {/* Report selector - Cards Grid */}
          </div>

          <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-4 mt-4">
            <div className="mt-4">
              <div className="mb-5 overflow-hidden rounded-2xl bg-gradient-to-r from-slate-950 via-blue-950 to-blue-800 p-4 text-white shadow-lg">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-200">Consultar día operativo</div>
                    <div className="mt-1 text-lg font-extrabold capitalize">
                      {formatOperationalDate(header.date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                    <div className="mt-1 text-xs text-blue-200">07:00 del día seleccionado a 06:59 del día siguiente</div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigateToOperationalDate(shiftISODate(header.date, -1))}
                      className="inline-flex h-10 items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 text-sm font-bold text-white hover:bg-white/20"
                      title="Día operativo anterior"
                    >
                      <span aria-hidden="true">←</span> Anterior
                    </button>
                    <input
                      type="date"
                      value={header.date}
                      max={businessToday}
                      onChange={(event) => navigateToOperationalDate(event.target.value)}
                      className="h-10 rounded-xl border border-white/30 bg-white px-3 text-sm font-semibold text-slate-900 focus:ring-2 focus:ring-blue-300"
                      aria-label="Seleccionar día operativo"
                    />
                    <button
                      type="button"
                      onClick={() => navigateToOperationalDate(shiftISODate(header.date, 1))}
                      disabled={header.date >= businessToday}
                      className="inline-flex h-10 items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 text-sm font-bold text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
                      title="Día operativo siguiente"
                    >
                      Siguiente <span aria-hidden="true">→</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigateToOperationalDate(businessToday)}
                      disabled={header.date === businessToday}
                      className="h-10 rounded-xl bg-blue-400 px-4 text-sm font-extrabold text-blue-950 shadow hover:bg-blue-300 disabled:cursor-default disabled:bg-white disabled:text-blue-900"
                    >
                      Hoy
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                  {recentOperationalDays.map((date) => {
                    const selected = date === header.date;
                    const today = date === businessToday;
                    return (
                      <button
                        key={date}
                        type="button"
                        onClick={() => navigateToOperationalDate(date)}
                        className={`min-w-[88px] rounded-xl border px-3 py-2 text-left transition ${selected ? 'border-white bg-white text-blue-950 shadow-lg' : 'border-white/15 bg-white/5 text-white hover:bg-white/15'}`}
                      >
                        <div className={`text-[10px] font-bold uppercase tracking-wide ${selected ? 'text-blue-600' : 'text-blue-200'}`}>
                          {today ? 'Hoy' : formatOperationalDate(date, { weekday: 'short' })}
                        </div>
                        <div className="mt-0.5 text-sm font-extrabold capitalize">
                          {formatOperationalDate(date, { day: 'numeric', month: 'short' })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <label className="text-xs font-semibold text-slate-600">
                  Seleccionar reporte existente ({visibleReports.length} reporte{visibleReports.length !== 1 ? 's' : ''} en esta vista)
                </label>
                <input
                  type="text"
                  placeholder="🔍 Buscar máquina, turno..."
                  value={reportSearchFilter}
                  onChange={(e) => setReportSearchFilter(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      return false;
                    }
                  }}
                  className="w-full sm:w-64 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mb-3 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                <button
                  type="button"
                  onClick={() => changeReportViewTab('mine')}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-md transition ${reportViewTab === 'mine' ? 'bg-blue-600 text-white shadow' : 'text-slate-700 hover:bg-slate-100'}`}
                >
                  Mis reportes ({mineReportsCount})
                </button>
                <button
                  type="button"
                  onClick={() => changeReportViewTab('others')}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-md transition ${reportViewTab === 'others' ? 'bg-blue-600 text-white shadow' : 'text-slate-700 hover:bg-slate-100'}`}
                >
                  Otras líneas ({otherReportsCount})
                </button>
              </div>

              <div className="mb-3 flex items-center justify-end">
                <button
                  type="button"
                  onClick={requestDeleteSelectedReport}
                  disabled={deletingReport || !canDeleteSelectedReport}
                  className="px-3 py-2 rounded-lg bg-white border border-red-300 text-red-700 font-semibold shadow-sm hover:bg-red-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:border-gray-200 disabled:cursor-not-allowed"
                  title={deleteRestrictionMessage}
                >
                  {deletingReport ? 'Borrando…' : '🗑 Borrar reporte activo'}
                </button>
              </div>
              
              <div className="space-y-7">
                {reportsByShift.map(({ shiftId, code, reports }) => (
                  <section key={shiftId} aria-labelledby={`report-shift-${shiftId}`}>
                    <div className="mb-3 flex items-center gap-3">
                      <h3 id={`report-shift-${shiftId}`} className="shrink-0 text-base font-bold text-slate-800">
                        Turno {code}
                      </h3>
                      <div className="h-px flex-1 bg-slate-300" aria-hidden="true" />
                      <span className="shrink-0 text-xs font-medium text-slate-500">
                        {reports.length} reporte{reports.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                      {reports.map((r) => {
                    const k = makeConversionReportKey({ site_id: siteId, date: r.date, line_id: r.line_id, shift_id: r.shift_id });
                    const line = lines.find((l) => String(l.id) === String(r.line_id));
                    const shift = shifts.find((s) => String(s.id) === String(r.shift_id));
                    const ownerEmployeeNumber = getReportOwnerEmployeeNumber(r);
                    const createdByPerson = people.find((p) => String(p.employee_number) === String(ownerEmployeeNumber));
                    const createdByName = createdByPerson?.name || (ownerEmployeeNumber ? `Emp. ${ownerEmployeeNumber}` : 'Unknown');
                    const isCurrentUser = currentUserEmployeeNumber && ownerEmployeeNumber && String(ownerEmployeeNumber) === String(currentUserEmployeeNumber);
                    const isSelected = k === reportSelectorValue;
                    const isClosed = r.status === 'closed';
                    const isMyInactiveReport = isCurrentUser && !isSelected && !isClosed;
                    const isPending = r?._local?.status === 'pending';
                    const updated = r.updated_at ? new Date(r.updated_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';
                    
                    // Color según estado - DESTACAR SI ES DEL USUARIO ACTUAL
                    let bgClass = 'bg-white border-slate-300';
                    let statusBadgeClass = 'bg-blue-100 text-blue-800';
                    let statusText = 'Abierto';
                    let borderClass = 'border-2';
                    let shadowClass = 'shadow';
                    
                    if (isCurrentUser && !isClosed && isSelected) {
                      bgClass = 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-500';
                      statusBadgeClass = 'bg-green-600 text-white';
                      statusText = '✓ Mi Reporte';
                      borderClass = 'border-3';
                      shadowClass = 'shadow-2xl';
                    } else if (isCurrentUser && !isClosed && !isSelected) {
                      bgClass = 'bg-gradient-to-br from-amber-50 to-orange-50 border-orange-400';
                      statusBadgeClass = 'bg-orange-500 text-white';
                      statusText = 'Mi reporte';
                      borderClass = 'border-3';
                      shadowClass = 'shadow-xl';
                    } else if (isClosed) {
                      bgClass = 'bg-slate-50 border-slate-300';
                      statusBadgeClass = 'bg-slate-200 text-slate-700';
                      statusText = 'Cerrado';
                    } else if (isPending) {
                      bgClass = 'bg-amber-50 border-amber-300';
                      statusBadgeClass = 'bg-amber-200 text-amber-800';
                      statusText = 'Pendiente';
                    } else if (isSelected) {
                      bgClass = 'bg-blue-50 border-blue-500';
                      statusBadgeClass = 'bg-blue-600 text-white';
                      statusText = 'Actual';
                    }
                    
                    return (
                      <div
                        key={k}
                        role="button"
                        tabIndex={0}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectExistingReport(r)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            selectExistingReport(r);
                          }
                        }}
                        className={`${bgClass} ${borderClass} rounded-xl p-4 text-left cursor-pointer hover:${shadowClass} transition-all duration-200 ${
                          isCurrentUser && isSelected
                            ? 'ring-4 ring-green-400 ring-offset-2 scale-105'
                            : isMyInactiveReport
                              ? 'ring-3 ring-orange-300 ring-offset-2'
                              : isSelected
                                ? 'ring-2 ring-blue-500 ring-offset-2'
                                : ''
                        } ${isClosed ? 'opacity-75' : ''} ${shadowClass}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="text-2xl font-bold text-slate-900 mb-1">
                              {line ? line.code : `Línea ${r.line_id}`}
                            </div>
                            <div className="text-sm font-medium text-slate-600">
                              {shift ? shift.code : `Turno ${r.shift_id}`}
                            </div>
                          </div>
                          <span className={`${statusBadgeClass} px-2 py-1 rounded-md text-xs font-semibold`}>
                            {statusText}
                          </span>
                        </div>
                        
                        {/* Mostrar nombre del operador */}
                        {r.created_by && (
                          <div className={`text-xs font-semibold mb-2 px-2 py-1 rounded-md inline-block ${
                            isCurrentUser && isSelected
                              ? 'bg-green-100 text-green-800'
                              : isMyInactiveReport
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-slate-100 text-slate-700'
                          }`}>
                            👤 {createdByName}
                          </div>
                        )}
                        
                        {updated && (
                          <div className="flex items-center gap-1 text-xs text-slate-500 mt-2">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Última actualización: {updated}
                          </div>
                        )}
                        
                        {isSelected && (
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <div className="flex items-center gap-1 text-xs font-semibold text-blue-700">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Reporte activo
                            </div>
                          </div>
                        )}
                      </div>
                    );
                      })}
                    </div>
                  </section>
                ))}

                {visibleReports.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <svg className="w-16 h-16 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                    </svg>
                    <p className="text-sm">Sin reportes en esta vista</p>
                    <p className="text-xs mt-1">Cambia de pestaña o ajusta el filtro de búsqueda</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div data-enter-tab-scope className="bg-white rounded-2xl shadow-md border border-slate-200 p-4 mt-4">
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Turno</label>
                <select
                  name="shift_id"
                  value={header.shift_id}
                  onChange={onHeaderChange}
                  disabled={readOnly}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                >
                  <option value="">Seleccionar…</option>
                  {shifts.map((s) => (
                    <option key={s.id} value={s.id}>{s.code} - {s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Máquina</label>
                <select
                  name="line_id"
                  value={header.line_id}
                  onChange={onHeaderChange}
                  disabled={readOnly}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                >
                  <option value="">Seleccionar…</option>
                  {lines.map((l) => (
                    <option key={l.id} value={l.id}>{l.code} - {l.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Week</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    name="week_override"
                    value={header.week_override}
                    onChange={onHeaderChange}
                    disabled={readOnly}
                    placeholder={effectiveWeek ? String(effectiveWeek) : ''}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                  />
                  <button
                    type="button"
                    onClick={() => setHeader((p) => ({ ...p, week_override: '' }))}
                    disabled={readOnly || !header.week_override}
                    className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 font-semibold border border-slate-200 hover:bg-slate-200 disabled:opacity-50"
                    title="Usar week calculado"
                  >
                    Auto
                  </button>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">Calculado: {computedWeek ?? '—'}</div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Estado</label>
                <select
                  value={header.status}
                  disabled
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 bg-slate-50"
                >
                  <option value="open">Abierto</option>
                  <option value="closed">Cerrado</option>
                </select>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Ajustador</label>
                <SearchableCatalogSelect
                  name="adjuster_id"
                  value={resolveEmployeeNumber(header, filteredPeople, 'adjuster')}
                  onChange={(value) => setEmployeeAssignment('adjuster', value)}
                  options={filteredPeople}
                  getOptionValue={getEmployeeNumber}
                  getOptionLabel={getEmployeeLabel}
                  getOptionAliases={getEmployeeAliases}
                  disabled={readOnly}
                  allowCustom
                  enterAsTab
                  customValuePattern={employeeNumberPattern}
                  placeholder="Buscar por número o nombre…"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Maquinista</label>
                <SearchableCatalogSelect
                  name="machinist_id"
                  value={resolveEmployeeNumber(header, filteredPeople, 'machinist')}
                  onChange={(value) => setEmployeeAssignment('machinist', value)}
                  options={filteredPeople}
                  getOptionValue={getEmployeeNumber}
                  getOptionLabel={getEmployeeLabel}
                  getOptionAliases={getEmployeeAliases}
                  disabled={readOnly}
                  allowCustom
                  enterAsTab
                  customValuePattern={employeeNumberPattern}
                  placeholder="Buscar por número o nombre…"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Recibidor</label>
                <SearchableCatalogSelect
                  name="receiver_id"
                  value={resolveEmployeeNumber(header, filteredPeople, 'receiver')}
                  onChange={(value) => setEmployeeAssignment('receiver', value)}
                  options={filteredPeople}
                  getOptionValue={getEmployeeNumber}
                  getOptionLabel={getEmployeeLabel}
                  getOptionAliases={getEmployeeAliases}
                  disabled={readOnly}
                  allowCustom
                  enterAsTab
                  customValuePattern={employeeNumberPattern}
                  placeholder="Buscar por número o nombre…"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Jefe de grupo</label>
                <SearchableCatalogSelect
                  name="group_leader_id"
                  value={resolveEmployeeNumber(header, filteredPeople, 'group_leader')}
                  onChange={(value) => setEmployeeAssignment('group_leader', value)}
                  options={filteredPeople}
                  getOptionValue={getEmployeeNumber}
                  getOptionLabel={getEmployeeLabel}
                  getOptionAliases={getEmployeeAliases}
                  disabled={readOnly}
                  allowCustom
                  enterAsTab
                  customValuePattern={employeeNumberPattern}
                  placeholder="Buscar por número o nombre…"
                />
              </div>
            </div>

            <div className="mt-3 text-sm text-slate-600">
              {selectedLine && selectedShift ? (
                <>
                  Capturando: <span className="font-semibold">{selectedLine.code}</span> · Turno <span className="font-semibold">{selectedShift.code}</span> · Semana <span className="font-semibold">{effectiveWeek ?? '—'}</span>
                </>
              ) : (
                <span className="text-amber-700 font-semibold">Selecciona Fecha, Turno y Máquina para comenzar.</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* Producción */}
        <div data-enter-tab-scope className="bg-white rounded-xl shadow border border-slate-200 overflow-hidden">
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
            <h3 className="text-lg font-bold text-blue-900">Producción</h3>
          </div>
          <div className="p-4">
            {!canCapture ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-6 text-slate-700">
                {captureBlockMessage}
              </div>
            ) : (
              <ConversionProductionGrid
                rows={rows}
                siteId={siteId}
                readOnly={readOnly}
                onChange={(next) => setRows(next.map(uppercaseConversionCapture))}
              />
            )}
          </div>
        </div>

        {/* Mano de Obra */}
        <div data-enter-tab-scope className="bg-white rounded-xl shadow border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
            <h3 className="text-lg font-bold text-slate-900">Mano de Obra A/X</h3>
          </div>
          <div className="p-4">
            {!canCapture ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-6 text-slate-700">
                {captureBlockMessage}
              </div>
            ) : (
              <ConversionDowntimeGrid
                events={downtime}
                downtimeCodes={downtimeCodes}
                readOnly={readOnly}
                onChange={(next) => setDowntime(next.map(uppercaseConversionCapture))}
                rows={rows}
              />
            )}
          </div>
        </div>

        {canCapture && <ConversionMaterialTraceability
          key={currentKey}
          materials={materials}
          rows={rows}
          readOnly={readOnly}
          onChange={setMaterials}
        />}

        {/* KPIs */}
        {canCapture && (
          <ConversionKpiPanel
            header={header}
            rows={rows}
            events={downtime}
            orderDataLookup={orderDataLookup}
            productsByItem={productsByItem}
          />
        )}
      </div>

      {showReopenModal && selectedReportMeta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (reopeningReport) return;
              setShowReopenModal(false);
              setReopenReason('');
            }}
          />
          <div className="relative w-full max-w-lg rounded-2xl border border-amber-200 bg-white shadow-2xl">
            <div className="rounded-t-2xl border-b border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-4">
              <h3 className="text-lg font-extrabold text-amber-900">Reabrir reporte cerrado</h3>
              <p className="mt-1 text-sm text-amber-800">El reporte volverá a ser editable y la acción quedará registrada.</p>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <div><span className="font-semibold">Fecha:</span> {selectedReportMeta.dateLabel}</div>
                <div><span className="font-semibold">Máquina:</span> {selectedReportMeta.lineLabel}</div>
                <div><span className="font-semibold">Turno:</span> {selectedReportMeta.shiftLabel}</div>
              </div>

              <label className="block text-sm font-semibold text-slate-800">
                Motivo de reapertura <span className="text-red-600">*</span>
                <textarea
                  value={reopenReason}
                  onChange={(event) => setReopenReason(event.target.value.slice(0, 500))}
                  disabled={reopeningReport}
                  rows={4}
                  autoFocus
                  placeholder="Ejemplo: corregir cantidad capturada en la orden…"
                  className="mt-2 w-full resize-none rounded-xl border border-slate-300 px-3 py-2 font-normal text-slate-900 focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                />
                <span className="mt-1 block text-right text-xs font-normal text-slate-500">{reopenReason.length}/500</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  setShowReopenModal(false);
                  setReopenReason('');
                }}
                disabled={reopeningReport}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={reopenSelectedReport}
                disabled={reopeningReport || reopenReason.trim().length < 3}
                className="rounded-lg bg-amber-500 px-4 py-2 font-extrabold text-amber-950 hover:bg-amber-400 disabled:bg-amber-200 disabled:text-amber-600"
              >
                {reopeningReport ? 'Reabriendo…' : 'Confirmar reapertura'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteReportModal && selectedReportMeta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (deletingReport) return;
              setShowDeleteReportModal(false);
              setDeleteConfirmCode('');
              setDeleteCodeInput('');
            }}
          />
          <div className="relative w-full max-w-lg rounded-2xl border border-red-200 bg-white shadow-2xl">
            <div className="px-5 py-4 border-b border-red-100 bg-gradient-to-r from-red-50 to-orange-50 rounded-t-2xl">
              <h3 className="text-lg font-extrabold text-red-800">⚠️ Confirmar eliminación</h3>
              <p className="mt-1 text-sm text-red-700">Esta acción elimina el badge y toda su información asociada.</p>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <div><span className="font-semibold">Fecha:</span> {selectedReportMeta.dateLabel}</div>
                <div><span className="font-semibold">Línea:</span> {selectedReportMeta.lineLabel}</div>
                <div><span className="font-semibold">Turno:</span> {selectedReportMeta.shiftLabel}</div>
              </div>

              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                Se borrarán: Producción, Mano de Obra A/X, Trazabilidad de materiales y KPIs de este reporte.
                <br />
                <span className="font-bold">No se puede deshacer.</span>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Para confirmar, escribe este código numérico:
                <div className="mt-1 text-xl font-extrabold tracking-widest text-amber-700">{deleteConfirmCode || '----'}</div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={deleteCodeInput}
                  onChange={(e) => setDeleteCodeInput(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  disabled={deletingReport}
                  placeholder="Escribe el código"
                  className="mt-2 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-amber-900 font-semibold focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteReportModal(false);
                  setDeleteConfirmCode('');
                  setDeleteCodeInput('');
                }}
                disabled={deletingReport}
                className="px-4 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={deleteSelectedReport}
                disabled={deletingReport || !deleteConfirmCode || String(deleteCodeInput).trim() !== String(deleteConfirmCode)}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:bg-red-300"
              >
                {deletingReport ? 'Borrando…' : 'Sí, borrar reporte'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversionCapture;
